import { useEffect, useState } from "react";
import type { Qubic, QubicKind, Subscription } from "api";
import { KindTabs } from "components/Store";
import type { LoadStatus } from "hooks/useProfile";
import { myQubicsTabs, productOf } from "./myqubics.model";
import { MyQubicsRow } from "./MyQubicsRow";

type MyQubicsPaneProps = {
  subscriptions: Subscription[];
  status: LoadStatus;
  qubics: Qubic[];
  /** Opens the Qubics Store on the given kind. */
  onOpenStore: (kind: QubicKind) => void;
  /** Called once when the pane opens, so the figures are current. */
  onOpened: () => void;
};

type EmptyBlockProps = {
  title: string;
  text: string;
  onOpen: () => void;
};

const EmptyBlock = ({ title, text, onOpen }: EmptyBlockProps) => (
  <div className="st-web--empty">
    <div className="pcx st-none">
      <div className="st-none__h">{title}</div>
      <p className="st-none__t">{text}</p>
      <button type="button" className="pcbtn pcbtn--main" onClick={onOpen}>
        Open Qubics Store
      </button>
    </div>
  </div>
);

/**
 * My Qubics: one row per tool the company holds — a limit set by a manager or
 * an API key bound to it, either is enough. Web integrations are not recorded,
 * so that side is always the way into the Store.
 */
export const MyQubicsPane = ({ subscriptions, status, qubics, onOpenStore, onOpened }: MyQubicsPaneProps) => {
  const [kind, setKind] = useState<QubicKind>("api");

  useEffect(() => onOpened(), [onOpened]);

  return (
    <div className="pc-pane">
      <div className="nw__hd">
        <div>
          <h1 className="nw__h">My Qubics</h1>
          <p className="nw__t">Manage the Qubics you already have access to.</p>
        </div>
      </div>

      <KindTabs
        tabs={myQubicsTabs(subscriptions.length)}
        showCounts={status === "ready"}
        active={kind}
        onSelect={setKind}
      />

      {kind === "api" && status === "loading" && <span className="pc-spin" aria-label="Loading" />}

      {kind === "api" && status === "failed" && (
        <p className="pc-note pc-note--bad">Could not load your qubics. Please refresh the page.</p>
      )}

      {kind === "api" && status === "unverified" && (
        <p className="pc-note">Verify your email address to see the qubics your company holds.</p>
      )}

      {kind === "api" && status === "ready" && subscriptions.length === 0 && (
        <EmptyBlock
          title="No API integrations yet"
          text="You have no qubics connected via API. Browse available qubics in Qubics Store."
          onOpen={() => onOpenStore("api")}
        />
      )}

      {kind === "api" && status === "ready" && subscriptions.length > 0 && (
        <div className="st-grid st-grid--sub">
          {subscriptions.map((subscription) => (
            <MyQubicsRow
              key={subscription.service_tool}
              subscription={subscription}
              product={productOf(subscription, qubics)}
            />
          ))}
        </div>
      )}

      {kind === "web" && (
        <EmptyBlock
          title="No web integrations yet"
          text="You have no web widgets connected. Browse ready-to-integrate widgets in Qubics Store."
          onOpen={() => onOpenStore("web")}
        />
      )}
    </div>
  );
};
